<template>
    <file-download-modal
        v-model="isShowDownloadExcelMenu"
        data-cy="download-file"
        :radio-items="[]"
        :label="$t('users_download')"
        :loading="isLoadingDownloadExcel"
        class="mb-2"
        @download="downloadFile"
    />
    <UserFilters
        v-model="filters"
        :has-active-filters="hasActiveFilters"
        :available-person-roles="availablePersonRoles"
        :companies="companies"
        @submit="applyFilters"
        @reset="resetFilters"
    />
    <v-data-table-server
        :headers="headers"
        :items="items"
        :items-per-page-options="[10, 15, 50, 100]"
        :options="options"
        :loading="isLoading"
        :items-length="serverItemsLength"
        density="compact"
        color="primary"
        class="elevation-1 table-server"
        data-cy="persons-table"
        must-sort
        @update:options="onUpdateTableOptions"
    >
        <template v-slot:item.NAME="{item}">
            <nuxt-link
                no-prefetch
                :to="{
                    name: personType,
                    params: {id: item.id, companyId: item.companyId},
                }"
                :data-cy="`link-person-page-${item.id}`"
            >
                <strong>{{ item.name || $t('user') }}</strong>
            </nuxt-link>
        </template>
        <template v-slot:item.active="{item}">
            {{ item.active ? $t('active') : $t('inactive') }}
        </template>
    </v-data-table-server>
</template>

<script lang="ts">
    import {Component} from 'vue-facing-decorator'
    import __PersonsTable, {PersonsTable as _PersonsTable} from '@/components/account/PersonsTable.vue'
    import {clean, clone, downloadData} from '~/utils/helpers'
    import UserFilters from '~/components/account/users/UserFilters.vue'
    import FileDownloadModal from '~/components/account/snippets/FileDownloadModal.vue'

    @Component({
        components: {FileDownloadModal, UserFilters},
        mixins: [__PersonsTable],
    })
    export class AllPersonsTable extends _PersonsTable {
        availablePersonRoles = []
        companies = []
        isShowDownloadExcelMenu = false
        isLoadingDownloadExcel = false

        async mounted() {
            await this.loadAvailablePersonRoles()
            await this.loadCompanies()
        }

        async load(): Promise<void> {
            const {itemsPerPage, page, sortBy} = this.options
            let key = 'NAME'
            let order = 'asc'
            if (sortBy?.length) {
                key = sortBy[0].key
                order = sortBy[0].order
            }
            const filters = clone(this.filters)
            clean(filters)
            if (filters.name) Object.assign(filters, {pattern: filters.name})

            await runtimeStore.loadPersons({
                sortingParam: key,
                ascending: order === 'asc',
                ...filters,
                limit: itemsPerPage,
                offset: itemsPerPage * (page - 1),
            })
        }

        async loadCompanies(): Promise<void> {
            try {
                const {companies} = await this.$api.companies.get({
                    active: this.filters.active,
                    shortResponse: true,
                    type: ['INTERNAL_SUPPLIER', 'TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR', 'GROUP'],
                })
                this.companies = companies.map(({id, name}) => ({value: id, title: name}))
            } catch (e) {
                this.companies = []
            }
        }

        async loadAvailablePersonRoles(): Promise<void> {
            try {
                const {availableRoleGroups} = await this.$api.availablePersonRoles.get()
                this.availablePersonRoles = availableRoleGroups.map(({name, value}) => ({value, title: name}))
            } catch (e) {
                this.availablePersonRoles = []
            }
        }

        get headers() {
            return [
                {title: this.$t('name'), value: 'name', minWidth: '120px', sortable: true, key: 'NAME'},
                {title: this.$t('login'), value: 'login', sortable: true, key: 'LOGIN'},
                {title: this.$t('company'), value: 'companyName'},
                {title: this.$t('role'), value: 'role', sortable: true, key: 'ROLE'},
                {title: this.$t('phone'), value: 'phone', minWidth: '160px', sortable: true, key: 'PHONE'},
                {title: this.$t('email'), value: 'email', sortable: true, key: 'EMAIL'},
                {title: this.$t('status'), value: 'active', minWidth: '80px'},
            ]
        }

        get items() {
            return runtimeStore.persons.map(person => {
                let firstName = person.firstName || ''
                let lastName = person.lastName || ''

                const role = person.role
                if (role && role?.includes('corp.')) {
                    firstName = person.firstNameLocalized || ''
                    lastName = person.lastNameLocalized || ''
                }

                return {
                    id: person.personId,
                    companyId: person.companyId,
                    name: `${person.prefix || ''} ${firstName} ${lastName}`.trim(),
                    login: person.login,
                    role: this.$t(`roles.${person.role.split('.')[1]}`),
                    email: person.email,
                    phone: person.contactPhone,
                    active: person.active === 'true',
                    department: person?.department?.name,
                    messenger: person?.icq,
                    skype: person.skype || '',
                    companyName: person.companyName,
                }
            })
        }

        async downloadFile(format) {
            try {
                this.isLoadingDownloadExcel = true

                const data = await this.$api.downloadPersons.get({
                    fileType: format.fileType,
                })
                await downloadData(data, 'users.xlsx')
                this.isShowDownloadExcelMenu = false
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoadingDownloadExcel = false
            }
        }
    }

    export default AllPersonsTable
</script>
